import { X, RotateCcw, MousePointer2, Zap, Eye, EyeOff } from 'lucide-react';
import Ghost from '../auth/Ghost.jsx';
import Tooltip from '../ui/Tooltip';
import { AI_READ_RESPONSES_LABEL } from '../../services/ai/responseAccess';
import { AI_PANEL_AUTORUN_KEY, AI_PANEL_INTERACTIVE_KEY } from './aiChatConstants';

/** Title, the three mode toggles, Clear session and Close. */
export function AiChatHeader({
  interactiveMode,
  setInteractiveMode,
  autoRunMode,
  setAutoRunMode,
  aiReadResponses,
  updatePreference,
  clearSession,
  closeLabel,
  onClose,
}) {
  return (
    <div className="ai-chat-header flex justify-between px-3 pb-2 border-b border-gray-200 dark:border-zinc-700 flex-shrink-0">
      <div className="ai-chat-header-title flex items-center gap-2 pt-2">
        <Ghost variant="classic" size={18} animated />
        <span className="font-semibold text-gray-800 dark:text-zinc-100 text-sm">AI Assistant</span>
      </div>
      <div className="ai-chat-header-actions flex items-center gap-1 pt-2">
        <Tooltip
          side="bottom"
          content={(
            <div className="discord-tooltip-body">
              {interactiveMode
                ? 'Interactive mode on — send_request uses Request Builder'
                : 'Interactive mode off — send_request runs in background'}
            </div>
          )}
        >
        <button
          type="button"
          aria-label="Interactive mode"
          className={`ai-chat-header-toggle${interactiveMode ? ' ai-chat-header-toggle--on' : ''}`}
          aria-pressed={interactiveMode}
          onClick={() => {
            setInteractiveMode((prev) => {
              const next = !prev;
              try {
                localStorage.setItem(AI_PANEL_INTERACTIVE_KEY, String(next));
              } catch {
                /* ignore */
              }
              return next;
            });
          }}
        >
          <MousePointer2 size={14} />
        </button>
        </Tooltip>
        <Tooltip
          side="bottom"
          content={(
            <div className="discord-tooltip-body">
              {autoRunMode
                ? 'Auto-run on — actions execute automatically (agentic)'
                : 'Auto-run off — confirm before each request/SQL/task action'}
            </div>
          )}
        >
        <button
          type="button"
          aria-label="Auto-run"
          className={`ai-chat-header-toggle${autoRunMode ? ' ai-chat-header-toggle--on' : ''}`}
          aria-pressed={autoRunMode}
          onClick={() => {
            setAutoRunMode((prev) => {
              const next = !prev;
              try {
                localStorage.setItem(AI_PANEL_AUTORUN_KEY, String(next));
              } catch {
                /* ignore */
              }
              return next;
            });
          }}
        >
          <Zap size={14} />
        </button>
        </Tooltip>
        <Tooltip
          side="bottom"
          content={(
            <div className="discord-tooltip-body">
              {aiReadResponses
                ? `${AI_READ_RESPONSES_LABEL}: on — the assistant can read response bodies (redacted)`
                : `${AI_READ_RESPONSES_LABEL}: off — the assistant sees status codes, never response bodies`}
            </div>
          )}
        >
        <button
          type="button"
          aria-label={AI_READ_RESPONSES_LABEL}
          className={`ai-chat-header-toggle${aiReadResponses ? ' ai-chat-header-toggle--on' : ''}`}
          aria-pressed={aiReadResponses}
          data-testid="ai-read-responses-toggle"
          onClick={() => updatePreference('aiReadResponses', !aiReadResponses)}
        >
          {aiReadResponses ? <Eye size={14} /> : <EyeOff size={14} />}
        </button>
        </Tooltip>
        <Tooltip side="bottom" content="Clear session">
        <button
          type="button"
          aria-label="Clear session"
          className="p-1.5 rounded-md bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors cursor-pointer"
          onClick={clearSession}
        >
          <RotateCcw size={14} />
        </button>
        </Tooltip>
        <Tooltip side="bottom" content={closeLabel}>
        <button
          type="button"
          aria-label={closeLabel}
          className="p-1.5 rounded-md bg-transparent text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:text-zinc-300 dark:hover:bg-zinc-800 dark:hover:text-zinc-100 transition-colors cursor-pointer"
          onClick={onClose}
        >
          <X size={14} />
        </button>
        </Tooltip>
      </div>
    </div>
  );
}
